import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';

import {
  SuspendedUseQueryOptions,
  SuspenseQueryOption,
  useCmsContent,
  useSuspendedQuery,
  useZepApiClient,
  UseZepApiQueryOptions,
} from '@zep/apis';
import { resolveContentId } from '@zep/next';
import { Currency, PricingZepfulResponse } from '@zep/types';

import { PlanTemplateType } from '../components';
import {
  CmsEntries,
  PlanCheckoutKeys,
  PlanKeys,
  singularSubscriptionQueryKeys,
} from '../consts';
import { SingularSubscriptionPaymentResponse } from '../types/PaymentTypes';
import {
  InvoiceDto,
  PaymentCard,
  PlanDetail,
  PlanProducts,
  SellingSpaceTemplateData,
  SpacePlan,
  SpacePlanListResponse,
  SpacePlanPaymentInfo,
  SpacePlans,
  SpacePlanSubscriptionDto,
  SpaceWithPlanDetail,
  SubscriptionPayment,
  SubscriptionPlan,
} from '../types/SubscriptionType';

import { useIsoCode } from './utilsService';

/**
 *  @alias {API_PLAN_PRODUCTS}
 * */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const usePlanProducts = (
  options?: SuspendedUseQueryOptions<
    PlanProducts,
    AxiosError,
    PlanProducts,
    string[]
  >,
) => {
  const client = useZepApiClient();
  const { data: countryCode } = useIsoCode({
    enabled: true,
    suspense: true,
  });

  return useSuspendedQuery(
    [PlanCheckoutKeys.Products],
    async () => {
      const { data: planProducts } = await client.get(
        '/space-plan-templates/selling',
        {
          params: {
            countryCode,
          },
        },
      );
      // 해당 CountryCode 에 맞는 데이터가 없을시 기본 US 가격으로 매칭해줍니다
      return planProducts.data;
    },
    {
      ...options,
      enabled: !!countryCode,
      // placeholderData: () => {
      //   const shareFakeData: Omit<
      //     PlanProduct,
      //     | 'id'
      //     | 'description'
      //     | 'createdAt'
      //     | 'updatedAt'
      //     | 'hashId'
      //     | 'option'
      //     | 'maxCcu'
      //   > = {
      //     name: '',
      //     currency: locale === 'ko' ? 'KRW' : 'USD',
      //     status: '0',
      //     originalPrice: 0,
      //     saleDiscountPrice: 0,
      //     saleDiscountRate: 0,
      //     salePrice: 0,
      //     subscriptionPeriod: 365,
      //     saleType: null,
      //   };
      //
      //   const fakeData: {
      //     [Plan in keyof PlanProducts]: Omit<
      //       PlanProduct,
      //       | 'description'
      //       | 'createdAt'
      //       | 'updatedAt'
      //       | 'hashId'
      //       | 'option'
      //       | 'salePrice'
      //       | 'subscriptionPeriod'
      //       | 'originalPrice'
      //       | 'saleType'
      //       | 'saleDiscountPrice'
      //     >[];
      //   } = {
      //     FREE: [
      //       {
      //         ...shareFakeData,
      //         maxCcu: 0,
      //         name: 'Free',
      //         id: 0,
      //       },
      //     ],
      //     BASIC: [
      //       {
      //         ...shareFakeData,
      //         maxCcu: 30,
      //         name: 'Basic',
      //         id: 1,
      //       },
      //     ],
      //     PRO: [
      //       {
      //         ...shareFakeData,
      //         maxCcu: 30,
      //         name: 'Pro',
      //         id: 2,
      //       },
      //     ],
      //     ENTERPRISE: [],
      //   };
      //
      //   return fakeData;
      // },
    },
  );
};

/**
 * @alias {API_PLAN_PRODUCTS_BY_SPACE}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const usePlanSpaceDetail = (
  spaceHashId: string,
  options?: SuspendedUseQueryOptions<
    SpaceWithPlanDetail,
    AxiosError,
    SpaceWithPlanDetail,
    string[]
  >,
) => {
  const client = useZepApiClient();

  return useSuspendedQuery(
    [...PlanKeys.spaceDetail(spaceHashId)],
    async () => {
      const { data: planSpaceDetail } = await client.get(
        '/v2/spaces/plans/detail',
        {
          params: {
            spaceHashId,
          },
        },
      );
      return planSpaceDetail.data;
    },
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const usePlanPaymentInfo = (
  spaceHashId: string,
  options?: UseQueryOptions<
    SpacePlanPaymentInfo,
    AxiosError,
    SpacePlanPaymentInfo,
    string[]
  >,
) => {
  const client = useZepApiClient();
  return useQuery(
    [...PlanCheckoutKeys.Space(spaceHashId)],
    async () => {
      const { data: planPaymentInfo } = await client.get(
        '/v2/spaces/plans/payment-info',
        {
          params: {
            spaceHashId,
          },
        },
      );
      return planPaymentInfo.data;
    },
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const usePlanDetail = (
  spacePlanHashId: string,
  options?: SuspendedUseQueryOptions<
    PlanDetail,
    AxiosError,
    PlanDetail,
    string[]
  >,
) => {
  const client = useZepApiClient();
  return useSuspendedQuery(
    [...PlanKeys.planDetail(spacePlanHashId)],
    async () => {
      const { data: planDetail } = await client.get('/space-plans/detail', {
        params: {
          spacePlanHashId,
        },
      });
      return planDetail.data;
    },
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useSellingSpacePlanTemplates = (
  {
    spaceHashId,
    templateType,
    subscriptionRequestHashId,
  }: {
    spaceHashId: string;
    templateType: PlanTemplateType;
    subscriptionRequestHashId?: string;
  },
  options?: SuspendedUseQueryOptions<
    {
      status: number;
      statusKey: string;
      data: SellingSpaceTemplateData;
    },
    AxiosError,
    SellingSpaceTemplateData
  >,
) => {
  const client = useZepApiClient();
  return useSuspendedQuery(
    [
      PlanCheckoutKeys.SellingSpace(
        spaceHashId,
        templateType,
        subscriptionRequestHashId,
      ),
    ],
    async () => {
      const { data: planProducts } = await client.get(
        '/space-plan-templates/selling/space',
        {
          params: { spaceHashId, templateType, subscriptionRequestHashId },
        },
      );
      return planProducts.data;
    },
    options,
  );
};

/**
 *  @alias {API_SPACE_BILLINGS_AUTH_TOKEN}
 * */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useRegisterPaymentCard = (
  options?: UseMutationOptions<
    { status: number },
    AxiosError,
    PaymentCard & {
      spaceHashId: string;
      paymentFlow?: 'card_registration' | 'card_change';
    }
  >,
) => {
  const client = useZepApiClient();
  return useMutation(
    [PlanCheckoutKeys.RegisterCard],
    payload => {
      return client.post('/space-billings/card', payload);
    },
    options,
  );
};

/**
 * @alias {API_SPACE_BILLINGS_CARD_NAME}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useSetPaymentCardName = (
  options?: UseMutationOptions<
    { status: number },
    AxiosError,
    {
      cardName: string;
      spaceHashId: string;
    }
  >,
) => {
  const client = useZepApiClient();
  return useMutation(
    [PlanCheckoutKeys.SetCardName],
    payload => {
      return client.put('/space-billings/card-name', payload);
    },
    options,
  );
};
/**
 * @alias {API_SPACE_BILLINGS_CARD}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useRemovePaymentCard = (
  options?: UseMutationOptions<{ status: number }, AxiosError, string>,
) => {
  const client = useZepApiClient();
  return useMutation(
    [PlanCheckoutKeys.RemoveCard],
    (spaceHashId: string) => {
      return client.delete('/space-billings/card', {
        data: {
          spaceHashId,
        },
      });
    },
    options,
  );
};

/**
 * @alias {API_SPACE_PLANS}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useSubscriptionPlan = (
  options?: UseMutationOptions<
    {
      status: number;
      data: {
        isUpgrade: boolean;
        spacePlan: SpacePlan;
        isZipCodeEdited: boolean;
        payment?: SubscriptionPayment;
      };
    },
    AxiosError,
    SubscriptionPlan & { isUpdated: boolean }
  >,
) => {
  const client = useZepApiClient();
  return useMutation(
    [PlanCheckoutKeys.Checkout],
    async payload => {
      if (payload.isUpdated) {
        const { data } = await client.put('/space-plans/subscribe', payload, {
          timeout: 30 * 1000,
        });
        return data;
      }

      const { data } = await client.post('/space-plans/subscribe', payload, {
        timeout: 30 * 1000,
      });
      return data;
    },
    options,
  );
};

/**
 * @alias {API_SPACE_PLANS_UNSUBSCRIBE}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useUnsubscribePlan = (
  spaceHashId: string,
  options?: UseMutationOptions<
    {
      status: number;
      statusKey: string;
      data: SpacePlanSubscriptionDto;
    },
    AxiosError
  >,
) => {
  const client = useZepApiClient();
  return useMutation(
    [...PlanKeys.unsubscribe],
    async () => {
      const { data } = await client.post('/space-plans/unsubscribe', {
        spaceHashId,
      });
      return data;
    },
    options,
  );
};

/**
 * @alias {API_SPACE_PLANS_EXPECT_PRICE}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useGetExpectPlanPrice = (
  spaceHashId: string,
  spacePlanTemplateId: number,
  options?: {
    queryKey: (string | number)[];
    enabled: boolean;
    placeholderData: () => {
      data: {
        originalPrice: number;
        saleDiscountPrice: number;
        saleType: string;
        finalPrice: number;
        totalDiscountPrice: number;
        isUpgrade: boolean;
        unusedPrice: number;
        asisPlanId: number;
        spacePlanTemplateId: number;
        salePrice: number;
        unusedDayCount: number;
        vatAmount: number;
        vatRate: number;
        priceBeforeVat: number;
      };
      status: number;
    };
  },
) => {
  const client = useZepApiClient();
  return useQuery(
    [...PlanCheckoutKeys.ExpectedPrice(spaceHashId, spacePlanTemplateId)],
    async () => {
      const { data } = await client.post('/space-plans/expect-price', {
        spaceHashId,
        spacePlanTemplateId,
      });

      return data;
    },
    {
      enabled: false,
      suspense: true,
      keepPreviousData: true,
      ...options,
    },
  );
};

// 일시납용 차액 조회
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useGetOnetimeExpectPlanPrice = (
  spacePlanTemplateId: number,
  spacePlanHashId?: string,
  options?: {
    queryKey: (string | number)[];
    enabled: boolean;
    placeholderData: () => {
      data: {
        originalPrice: number;
        saleDiscountPrice: number;
        saleType: string;
        finalPrice: number;
        totalDiscountPrice: number;
        isUpgrade: boolean;
        unusedPrice: number;
        asisPlanId: number;
        spacePlanTemplateId: number;
        salePrice: number;
        unusedDayCount: number;
        vatAmount: number;
        vatRate: number;
        priceBeforeVat: number;
      };
      status: number;
    };
  },
) => {
  const client = useZepApiClient();
  return useQuery(
    [
      ...PlanCheckoutKeys.ExpectedOnetimePrice(
        spacePlanHashId ?? '',
        spacePlanTemplateId,
      ),
    ],
    async () => {
      const { data } = await client.post('/space-plans/onetime/expect-price', {
        spacePlanHashId,
        spacePlanTemplateId,
      });
      return data;
    },
    {
      enabled: false,
      suspense: true,
      keepPreviousData: true,
      ...options,
    },
  );
};

/**
 * @alias {API_SPACE_BILLINGS_AUTH_TOKEN}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const usePaypleAuthTokenMutation = (
  options?: UseMutationOptions<
    {
      status: number;
      data: {
        accessToken: string;
        code: string;
        expiresIn: string;
        message: string;
        result: string;
        tokenType: string;
      };
    },
    AxiosError,
    {
      currency: Currency;
      type:
        | 'chargeZem'
        | 'subscription'
        | 'business'
        | 'singularSubscription'
        | 'onetime';
    }
  >,
) => {
  const client = useZepApiClient();
  return useMutation(
    [PlanCheckoutKeys.PaypleAuthToken],
    async ({ currency, type }) => {
      const { data } = await client.get(
        type === 'chargeZem'
          ? '/v2/cash/buy/payple/auth-token'
          : type === 'singularSubscription'
          ? '/space-plans/payments/requests/payple/auth-token'
          : '/space-billings/auth-token',
        {
          ...(type === 'chargeZem' && {
            params: {
              currency,
            },
          }),
        },
      );

      return data;
    },
    options,
  );
};

/**
 * @alias {space-subscription/cancel-unsubscribe}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useCancelUnsubscribe = (
  spaceHashId: string,
  options?: UseMutationOptions<SpacePlanListResponse, AxiosError>,
) => {
  const client = useZepApiClient();
  return useMutation(
    [...PlanKeys.cancelUnsubscribe],
    async () => {
      const { data } = await client.post('/space-plans/cancel-unsubscribe', {
        spaceHashId,
      });
      return data;
    },
    options,
  );
};

/**
 * @alias {space-subscription/cancel-downgrade}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useCancelDowngrade = (
  spaceHashId: string,
  options?: UseMutationOptions<SpacePlanListResponse, AxiosError>,
) => {
  const client = useZepApiClient();
  return useMutation(
    [...PlanKeys.cancelDowngrade],
    async () => {
      const { data } = await client.post('/space-plans/cancel-downgrade', {
        spaceHashId,
      });
      return data;
    },
    options,
  );
};

/**
 * @alias {API_BILLING_PAYMENTS}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useGetSpacePlans = (
  spaceHashId: string,
  options?: SuspendedUseQueryOptions<SpacePlans, AxiosError>,
) => {
  const client = useZepApiClient();
  return useSuspendedQuery(
    [...PlanCheckoutKeys.BillingPayment(spaceHashId)],
    async () => {
      const { data: plans } = await client.get('/v2/spaces/plans', {
        params: {
          spaceHashId,
        },
      });
      return plans.data;
    },
    options,
  );
};

/**
 * @alias {API_BILLING_PAYMENTS_INVOICE}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useGetBillingInvoice = (
  spacePlanHashId: string,
  options?: UseQueryOptions<
    { status: number; statusKey: string; data: InvoiceDto },
    AxiosError
  >,
) => {
  const client = useZepApiClient();
  const { data } = useSuspendedQuery(
    [PlanCheckoutKeys.BillingInvoice],
    async () => {
      const { data } = await client.get(`/space-plans/invoice`, {
        params: {
          spacePlanHashId,
        },
      });
      return data;
    },
    options,
  );
  return data;
};

/**
 * 해외 결제 카드 빌링 키 등록 및 수정 모두 해당 콜백 URL을 사용합니다.
 * @alias {API_SPACE_BILLINGS_CARD_CALLBACK}
 * */
export const PostBillingCardCallBack = `${process.env.NEXT_PUBLIC_BASE_URL}/api/space-billings/card/payple-callback`;

export function usePrice(
  options?: UseZepApiQueryOptions<PricingZepfulResponse>,
) {
  const { locale = 'en' } = useRouter();
  // options 를 그대로 넘긴다 — useCmsContent 기본값이 기존 useZepetofulContents 와
  // 동일(useErrorBoundary:false / staleTime 10분 / retry 2)해서 동작이 보존된다.
  // 쿼리키가 pages/pricing 의 withCmsContent 프리페치와 같아야 하이드레이션이 붙으므로
  // 여기서도 resolveContentId 를 쓴다.
  return useCmsContent<PricingZepfulResponse>(
    resolveContentId(CmsEntries.PRICING, locale),
    options,
  );
}

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useSingularSubscriptionCustomerName = (
  paymentRequestHashId: string,
  options?: SuspenseQueryOption<string>,
) => {
  const client = useZepApiClient();
  return useSuspendedQuery(
    [singularSubscriptionQueryKeys.CHECK_PAYMENT_HASH_ID, paymentRequestHashId],
    async () => {
      const { data } = await client.get('/space-plans/payments/requests/name', {
        params: {
          paymentRequestHashId,
        },
      });

      return data.data;
    },
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useSingularSubscriptionPaymentMutation = (
  options?: UseMutationOptions<
    { status: number; data: SingularSubscriptionPaymentResponse },
    AxiosError,
    { paymentRequestHashId: string; email: string }
  >,
) => {
  const client = useZepApiClient();

  return useMutation(
    [singularSubscriptionQueryKeys.CHECK_PAYMENT_PERMISSION],
    async ({ email, paymentRequestHashId }) => {
      const { data: paymentData } = await client.get(
        '/space-plans/payments/requests',
        {
          params: {
            paymentRequestHashId,
            email,
          },
        },
      );
      return paymentData;
    },
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useSingularSubscriptionPaymentResult = (
  {
    paymentRequestHashId,
    email,
  }: { paymentRequestHashId: string; email: string },
  options?: SuspenseQueryOption<SingularSubscriptionPaymentResponse>,
) => {
  const client = useZepApiClient();
  return useSuspendedQuery(
    [
      singularSubscriptionQueryKeys.GET_PAYMENT_RESULT,
      paymentRequestHashId,
      email,
    ],
    async () => {
      const { data } = await client.get('/space-plans/payments/requests', {
        params: {
          paymentRequestHashId,
          email,
        },
      });
      return data.data;
    },
    { ...options, enabled: !!paymentRequestHashId && !!email },
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useUpdateZipCode = (
  options?: UseMutationOptions<
    {
      status: 0;
      statusKey: string;
      data: string;
    },
    AxiosError,
    { spaceHashId: string; zipCode: string | null }
  >,
) => {
  const client = useZepApiClient();
  return useMutation(
    [...PlanCheckoutKeys.SetZipCode('payment')],
    async ({
      spaceHashId,
      zipCode,
    }: {
      spaceHashId: string;
      zipCode: string | null;
    }) => {
      const { data } = await client.put('/space-billings/zip-code', {
        spaceHashId,
        zipCode,
      });
      return data;
    },
    options,
  );
};

export const SingularSubscriptionPaymentTossSuccessCallbackUrl = `/api/space-plans/payments/requests/toss/callback/success`;
export const SingularSubscriptionPaymentPaypleCallbackUrl =
  '/api/space-plans/payments/requests/payple/callback';
export const OnetimePaymentPaypleCallbackUrl = `/api/space-plans/product-onetime/payple/callback`;
